import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { fontRoboto } from "shared/lib/styles";
import type { WithStickyStackedHeightProps } from "shared/lib/styles";

import {
  applyStylesOnSlotCollapsed,
  autoCollapseAdSlotStyles,
} from "scmp-app/components/advertisement/styles";

import { Slot } from "./slot";

export type StyledProps = WithStickyStackedHeightProps & {
  $breakpoint?: string;
  $currentPosition?: string;
  $isBreakpoint?: boolean;
  $isDisabled?: boolean;
  $minContainerBlockSize?: number;
  $minContainerInlineSize?: number;
  $minContainerSize?: {
    $blockSize?: number;
    $inlineSize?: number;
  };
  $withAutoContainerSize?: boolean;
  $withBackground?: boolean;
  $withLabel?: boolean;
  $withSticky?: {
    $blockSize?: number;
    $inlineSize?: number;
    $stickyLabel?: boolean;
  };
};

type WithLabelBackgroundProps = {
  $withLabelBackground?: boolean;
};

export const Label = styled.div<WithLabelBackgroundProps>`
  inline-size: 100%;
  padding-block: 6px;

  color: #717171;
  font-weight: 400;
  font-size: 10px;
  font-family: ${fontRoboto};
  line-height: 12px;
  text-align: center;
  text-transform: uppercase;

  ${props =>
    props.$withLabelBackground &&
    css`
      background-color: #f8f8f8;
    `}
`;

export const AdSlotContainer = styled.div``;

export const StyledSlot = styled(Slot)`
  inline-size: 100%;
  block-size: 100%;
`;

export const Container = styled.div<StyledProps>`
  flex-flow: column;
  justify-content: ${props => (props.$withLabel ? "flex-start" : "center")};
  align-items: center;

  inline-size: 100%;
  block-size: 100%;

  ${props =>
    props.$isBreakpoint &&
    !props.$isDisabled &&
    css`
      ${applyStylesOnSlotCollapsed(css`
        &,
        ${AdSlotContainer} {
          ${autoCollapseAdSlotStyles}
        }
        ${Label} {
          display: none;
        }
      `)}
    `}

  ${props =>
    props.$withBackground &&
    css`
      padding-block: 0 1px;
      padding-inline: 1px;

      background-color: #f8f8f8;
    `}

  ${props => {
    if (props.$withSticky) {
      return props.$withSticky.$stickyLabel
        ? css`
            position: sticky;
            inset-block-start: ${props.$currentPosition === "sticky"
              ? props.$stickyStackedHeight
              : 0}px;

            block-size: fit-content;
          `
        : css`
            min-block-size: ${props.$withSticky.$blockSize}px;

            ${AdSlotContainer} {
              position: sticky;
              inset-block-start: ${props.$currentPosition === "sticky"
                ? props.$stickyStackedHeight
                : 0}px;
            }
          `;
    }
  }}

  ${props =>
    props.$breakpoint
      ? css`
          display: none;

          ${props.$breakpoint} {
            display: flex;
          }
        `
      : css`
          display: flex;
        `}

  ${Label} {
    display: ${props => (props.$withLabel ? "block" : "none")};
  }

  ${AdSlotContainer} {
    inline-size: ${props => (props.$withAutoContainerSize ? "100%" : "auto")};
    block-size: ${props => (props.$withAutoContainerSize ? "100%" : "auto")};
    min-inline-size: ${props =>
      props.$minContainerSize?.$inlineSize ? `${props.$minContainerSize.$inlineSize}px` : "unset"};
    min-block-size: ${props =>
      props.$minContainerSize?.$blockSize ? `${props.$minContainerSize.$blockSize}px` : "unset"};
  }

  ${StyledSlot} {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;
